import { z } from 'zod';

import { TFieldSchema } from '../../../PaymentProviders/schemas/payment-provider-schema';

const formatLabel = (id: string) =>
  id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Builds the validation schema for a provider's client-scope fields.
 *
 * The field list is dynamic — it comes from the provider's catalog schema — so
 * the schema has to be built per provider rather than declared statically.
 *
 * The backend deliberately does NOT enforce required fields ("ops sometimes
 * saves partial rows"), and a partial row reads as "not configured" on the
 * kiosk, which then refuses card payment. So this is the only thing standing
 * between ops and a silently dead terminal.
 *
 * `encryptedKeys` carries the fields that already hold a stored secret. Those
 * read back as a mask, and a blank input means "keep the existing value" — so a
 * required secret that is already stored must pass validation when left empty.
 */
export const buildClientFieldsSchema = (
  clientFields: TFieldSchema[],
  encryptedKeys: string[] = [],
) => {
  const shape = clientFields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
    acc[field.id] = z.string().optional();
    return acc;
  }, {});

  return z.object(shape).superRefine((values, ctx) => {
    clientFields.forEach((field) => {
      if (!field.required) return;

      const typed = (values as Record<string, string | undefined>)[field.id];
      if (typed && typed.trim() !== '') return;

      // Blank is acceptable only when a secret is already stored for this field.
      if (encryptedKeys.includes(field.id)) return;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field.id],
        message: `${field.label || formatLabel(field.id)} is required`,
      });
    });
  });
};

export type TClientFieldsForm = Record<string, string | undefined>;
